import { fetchApi } from './client';
import type {
    CveSortField,
    MatchRunResult,
    PaginatedResponse,
    ResetResult,
    SyncStatus,
    VendorCveMatch,
} from './types';

interface GetMatchesParams {
    // Numeric catalog-product-candidate id — the canonical vendor key (F3).
    // Resolves candidate-only rows whose compound id degrades to `::<slug>`.
    // Backend prefers it over vendor_directory_id.
    catalog_product_candidate_id?: number;
    vendor_directory_id?: string;
    // Match lifecycle status: 'new' | 'published' | 'failed'.
    status?: string;
    cvss_severity?: string;
    match_signal?: string;
    // KEV-only toggle — the API expects the literal string 'true' | 'false'.
    is_kev?: 'true' | 'false';
    sort?: CveSortField;
    order?: 'asc' | 'desc';
    include_cve_details?: boolean;
    limit?: number;
    offset?: number;
}

/**
 * Get CVE matches with optional filtering.
 */
export async function getMatches(
    params: GetMatchesParams = {},
): Promise<PaginatedResponse<VendorCveMatch>> {
    const searchParams = new URLSearchParams();

    if (params.catalog_product_candidate_id !== undefined) {
        searchParams.set(
            'catalog_product_candidate_id',
            String(params.catalog_product_candidate_id),
        );
    }

    if (params.vendor_directory_id) {
        searchParams.set('vendor_directory_id', params.vendor_directory_id);
    }

    if (params.status) {
        searchParams.set('status', params.status);
    }

    if (params.cvss_severity) {
        searchParams.set('cvss_severity', params.cvss_severity);
    }

    if (params.match_signal) {
        searchParams.set('match_signal', params.match_signal);
    }

    if (params.is_kev) {
        searchParams.set('is_kev', params.is_kev);
    }

    if (params.sort) {
        searchParams.set('sort', params.sort);
    }

    if (params.order) {
        searchParams.set('order', params.order);
    }

    if (params.include_cve_details) {
        searchParams.set('include_cve_details', 'true');
    }

    if (params.limit !== undefined) {
        searchParams.set('limit', params.limit.toString());
    }

    if (params.offset !== undefined) {
        searchParams.set('offset', params.offset.toString());
    }

    const queryString = searchParams.toString();
    const path = queryString ? `/api/monitoring/matches?${queryString}` : '/api/monitoring/matches';

    return fetchApi<PaginatedResponse<VendorCveMatch>>(path);
}

/**
 * Get the current sync status for CVE and KEV data sources.
 */
export async function getSyncStatus(): Promise<SyncStatus> {
    return fetchApi<SyncStatus>('/api/monitoring/status');
}

/**
 * Run the full matching engine across all vendors.
 */
export async function runMatch(): Promise<MatchRunResult> {
    return fetchApi<MatchRunResult>('/api/monitoring/run/match', {
        method: 'POST',
    });
}

/**
 * Run the matching engine scoped to a single candidate (by numeric id).
 */
export async function runCandidateMatch(
    catalogProductCandidateId: number,
): Promise<MatchRunResult> {
    return fetchApi<MatchRunResult>(
        `/api/monitoring/run/match/candidate/${String(catalogProductCandidateId)}`,
        { method: 'POST' },
    );
}

/**
 * Full-corpus re-match scoped to a single candidate (VI-556). Ignores the global
 * cursor so PRE-EXISTING CVEs are matched (the incremental variant above skips
 * them), then publishes + emits the monitoring event. Called after enabling
 * monitoring for a vendor to backfill its history.
 */
export async function runCandidateFullMatch(
    catalogProductCandidateId: number,
): Promise<MatchRunResult> {
    return fetchApi<MatchRunResult>(
        `/api/monitoring/run/match/candidate/${String(catalogProductCandidateId)}/full`,
        { method: 'POST' },
    );
}

/**
 * Publish new matches to SQS.
 */
export async function publish(): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>('/api/monitoring/run/publish', {
        method: 'POST',
    });
}

/**
 * Delete all matches for a specific candidate (per-candidate demo reset).
 */
export async function resetCandidateMatches(
    catalogProductCandidateId: number,
): Promise<ResetResult> {
    return fetchApi<ResetResult>(
        `/api/monitoring/matches/candidate/${String(catalogProductCandidateId)}`,
        { method: 'DELETE' },
    );
}

/**
 * Global demo reset: delete all matches and reset matching cursor.
 */
export async function resetAllMatches(): Promise<ResetResult> {
    return fetchApi<ResetResult>('/api/monitoring/reset', {
        method: 'POST',
    });
}
